
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { GoogleMapPreview } from "./GoogleMapPreview";
import { AddressAutocomplete } from "./AddressAutocomplete";
import { cn } from "@/lib/utils";
import { FieldError } from "./FieldError";

interface PropertyLocationProps {
  address: string;
  setAddress: (value: string) => void;
  city: string;
  setCity: (value: string) => void;
  state: string;
  setState: (value: string) => void;
  zipCode: string;
  setZipCode: (value: string) => void;
  latitude?: number | null;
  longitude?: number | null;
  onCoordinatesChange?: (lat: number, lng: number) => void;
  errors?: Record<string, string>;
  clearFieldError?: (field: string) => void;
}

export const PropertyLocationSection = ({
  address,
  setAddress,
  city,
  setCity,
  state,
  setState,
  zipCode,
  setZipCode,
  latitude,
  longitude,
  onCoordinatesChange,
  errors = {},
  clearFieldError,
}: PropertyLocationProps) => {

  const handleAddressSelect = (addressData: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
  }) => {
    setAddress(addressData.address);
    setCity(addressData.city);
    setState(addressData.state);
    setZipCode(addressData.zipCode);
    clearFieldError?.("address");
    clearFieldError?.("city");
    clearFieldError?.("state");
    clearFieldError?.("zipCode");
  };

  const handleAddressInputChange = (value: string) => {
    setAddress(value);
    clearFieldError?.("address");
  };

  const errCls = (field: string) =>
    errors[field] ? "border-destructive focus-visible:ring-destructive" : "";

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Property Location</h2>
          <Separator />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <AddressAutocomplete 
                initialAddress={address}
                onAddressSelect={handleAddressSelect}
                onInputChange={handleAddressInputChange}
                className={cn(errCls("address"))}
              />
              <FieldError message={errors.address} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">City*</Label>
              <Input 
                id="city" 
                placeholder="Philadelphia" 
                required 
                aria-invalid={!!errors.city}
                className={cn("bg-muted", errCls("city"))}
                value={city}
                onChange={(e) => { setCity(e.target.value); clearFieldError?.("city"); }} 
              />
              <FieldError message={errors.city} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">State*</Label>
              <Input 
                id="state" 
                placeholder="PA" 
                required 
                aria-invalid={!!errors.state}
                className={cn("bg-muted", errCls("state"))}
                value={state}
                onChange={(e) => { setState(e.target.value); clearFieldError?.("state"); }} 
              />
              <FieldError message={errors.state} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="zipCode">Zip Code*</Label>
              <Input 
                id="zipCode" 
                placeholder="19103" 
                required 
                aria-invalid={!!errors.zipCode}
                className={cn("bg-muted", errCls("zipCode"))}
                value={zipCode}
                onChange={(e) => { setZipCode(e.target.value); clearFieldError?.("zipCode"); }} 
              />
              <FieldError message={errors.zipCode} />
            </div>
          </div>
          
          {/* Google Maps integration */}
          <div className="mt-4">
            <Label>Map Preview</Label>
            <div className="mt-2">
              {(address || city || state || zipCode) ? (
                <GoogleMapPreview 
                  address={address} 
                  city={city} 
                  state={state} 
                  zipCode={zipCode}
                  initialLatitude={latitude}
                  initialLongitude={longitude}
                  onCoordinatesChange={onCoordinatesChange}
                />
              ) : (
                <div className="border border-border rounded-md h-[200px] bg-muted flex items-center justify-center">
                  <p className="text-muted-foreground">Enter an address to see location</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
